import { toast } from '@/components/ui/use-toast'
import { useCreateEstateMedia } from '@/estateMedia/hooks/useCreateEstateMedia'
import {
  FileUploadDialog,
  FileUploadDialogProps,
  OnUploadProps,
} from '@/file/FileUploadDialog'
import { EstateMediaTypeFragment } from '@gql'
import React, { ReactNode, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { EstateMediaTypeNameTranslation } from './EstateMediaTypeNameTranslation'

export type EstateMediaFileUploadDialogProps = Omit<
  FileUploadDialogProps,
  'onUpload'
> & {
  children?: ReactNode
  onUpload?: () => void
  type: EstateMediaTypeFragment
  estateId: string
  showToasts?: boolean
}
export const EstateMediaFileUploadDialog = ({
  children,
  onUpload,
  type,
  canSetCustomName,
  estateId,
  showToasts,
  ...props
}: EstateMediaFileUploadDialogProps) => {
  const { t } = useTranslation()
  const { createEstateMedia, data, loading, error } = useCreateEstateMedia()

  const handleFileUpload = async ({ id, customName }: OnUploadProps) => {
    if (!id) {
      toast({
        variant: 'destructive',
        description: t('EstateMediaFileUploadDialog.toast.error'),
      })
      return
    }
    await createEstateMedia({
      variables: {
        values: {
          fileId: id,
          typeId: type.id,
          name: canSetCustomName ? customName : undefined,
          estateId: estateId,
        },
      },
    })
    onUpload?.()
  }

  useEffect(() => {
    if (showToasts && error) {
      toast({
        variant: 'destructive',
        description: t('EstateMediaFileUploadDialog.toast.error'),
      })
    }
  }, [error])

  useEffect(() => {
    if (showToasts && data) {
      toast({
        variant: 'success',
        description: t('EstateMediaFileUploadDialog.toast.success'),
      })
    }
  }, [data])

  return (
    <FileUploadDialog
      description={<EstateMediaTypeNameTranslation type={type} />}
      onUpload={handleFileUpload}
      canSetCustomName={canSetCustomName}
      loading={loading}
      {...props}
    >
      {children}
    </FileUploadDialog>
  )
}
